/**
 * Shared constants for Sendly.
 * Includes difficulty levels, hold colors, default settings, themes, and font config.
 */

// Platform default accent color — single source of truth for fallback accent everywhere
export const DEFAULT_ACCENT = "#376f8b";

// Default gym meta document - identity, branding, features, stats (stored in gyms/{gymId})
export const DEFAULT_GYM_META = {
  name: "Sendly",
  slug: "",
  description: "",
  address: "",
  city: "",
  country: "HU",
  location: null,
  logoUrl: "",
  coverImageUrl: "",
  contactEmail: "",
  contactPhone: "",
  website: "",
  socialLinks: { facebook: "", instagram: "" },
  timezone: null, // IANA timezone string, e.g. "Europe/Budapest" — used for opening hours evaluation
  openingHours: { mon: null, tue: null, wed: null, thu: null, fri: null, sat: null, sun: null },
  accentColor: DEFAULT_ACCENT,
  plan: "pro",
  planExpiresAt: null,
  features: {
    tvDashboard: false, friends: false, competitions: false, events: false,
    customBranding: false, dbMaintenance: false, setterStats: false, gymPwa: false,
    advancedStats: false, publicWidget: false,
  },
  // No `stats` field: gym counters live in the sharded counter subcollection
  // (`gyms/{gymId}/counters/shard_*`, the runtime-maintained source of truth)
  // and active-route totals come from live `.count()`. The old parent-doc
  // `stats` snapshot was never updated at runtime, so it has been retired.
  isPublic: true,
  isActive: true,
};

// Default operational settings (stored in gyms/{gymId}.settings)
export const DEFAULT_SETTINGS = {
  mapImageUrl: "",
  mapWidth: 520,
  mapHeight: 480,
  tvSlideDuration: 12,
  // Days a freshly-set route keeps its "ÚJ" badge. Gym-wide: honored by the
  // app map AND the TV dashboard. Historical field name was `tvNewRouteDays`;
  // see `resolveNewRouteDays` below for the backward-compat read path.
  newRouteDays: 7,
  tvTheme: "dark",
  tvLanguage: "hu",
};

/**
 * Resolve the "new route highlight" threshold from a settings doc. Prefers
 * the current `newRouteDays` field and falls back to the legacy
 * `tvNewRouteDays` so gyms that haven't re-saved settings since the rename
 * keep working. Non-positive / missing values fall through to 7.
 */
export function resolveNewRouteDays(settings) {
  const v = settings?.newRouteDays ?? settings?.tvNewRouteDays;
  return Number.isFinite(v) && v > 0 ? v : 7;
}

/**
 * Inclusive lower-bound date string (`YYYY-MM-DD`) for the "new route"
 * window: a route counts as new when its `createdAt` is `>=` this value.
 *
 * Day granularity is deliberate and the ONLY correct method here — a route's
 * `createdAt` is a gym-timezone date string with no time component (see
 * `useMainApp` → `todayStr`). Comparing by milliseconds (`Date.now() - N*86400000`)
 * only re-parses that date as UTC midnight, adding timezone skew and an
 * off-by-one mid-day flip. This single helper is shared by the app map badge
 * (ZoomableMap) and every TV slide so they always agree on which routes are new.
 *
 * Pass `resolveNewRouteDays(settings)` as `newDays`.
 */
export function newRouteCutoff(newDays, today) {
  const n = Number.isFinite(newDays) && newDays > 0 ? newDays : 7;
  // Anchor the N-day window on the gym's "today" (route.createdAt is written in
  // the gym timezone) rather than UTC, so a route doesn't flip "new" a few hours
  // early/late around midnight. `today` = gym-tz "YYYY-MM-DD"; falls back to UTC
  // today when omitted. Noon-UTC + UTC arithmetic avoids DST edge flips.
  const base = (typeof today === "string" && /^\d{4}-\d{2}-\d{2}$/.test(today))
    ? today
    : new Date().toISOString().split("T")[0];
  const d = new Date(base + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().split("T")[0];
}

// The built-in difficulty ladder and the universal grade scale live in
// `@sendly/shared` (the Cloud Functions read the same rows); `DIFFICULTY` is
// the client's name for the package's `DEFAULT_DIFFICULTY`.
export { DEFAULT_DIFFICULTY as DIFFICULTY, GRADE_SCALE } from "@sendly/shared";

// Hold (climbing grip) color options
export const HOLD_COLORS = {
  pink:   { label: "Rózsaszín", color: "#ec4899" , order: 0 },
  orange: { label: "Narancs",   color: "#f97316" , order: 1 },
  yellow: { label: "Sárga",     color: "#eab308" , order: 2 },
  green:  { label: "Zöld",      color: "#22c55e" , order: 3 },
  blue:   { label: "Kék",       color: "#3b82f6" , order: 4 },
  red:    { label: "Piros",     color: "#ef4444" , order: 5 },
  purple: { label: "Lila",      color: "#a855f7" , order: 6 },
  white:  { label: "Fehér",     color: "#e5e5e5" , order: 7 },
  black:  { label: "Fekete",    color: "#404040" , order: 8 },
};

// Light and dark theme palettes
export const THEMES = {
  light: {
    bg: "#fafafa", card: "#ffffff", text: "#1a1a1a", textMuted: "#6b6b6b",
    border: "#e0e0e0", borderLight: "#f0f0f0", headerBg: "#ffffff",
    inputBg: "#ffffff", pillBg: "#ffffff", mapBg: "#f5f5f0",
    // Alternating row tint for long lists read from a distance (TV rankings).
    // Deliberately near the threshold of noticing: it should give the eye a
    // rhythm to track a name across to its score, not stripe the screen.
    // An rgba rather than a hex so it works over whatever it happens to sit
    // on — the page background in one list, a card in another.
    zebra: "rgba(0,0,0,0.035)",
  },
  dark: {
    bg: "#111111", card: "#1e1e1e", text: "#f0f0f0", textMuted: "#999999",
    border: "#333333", borderLight: "#282828", headerBg: "#1a1a1a",
    inputBg: "#2a2a2a", pillBg: "#2a2a2a", mapBg: "#1a1a1a",
    zebra: "rgba(255,255,255,0.04)",
  },
};

// Global font family used throughout the app
export const F = "'Space Grotesk', system-ui, sans-serif";

// Podium markers, keyed by PLACE (1-based), not by array index. Thirteen call
// sites wrote these three glyphs by hand in two shapes — an array indexed with
// `rank - 1` and an object keyed by place — and the array form put the off-by-one
// in every caller. Keyed by place there is nothing to get wrong: `PLACE_MEDALS[rank]`
// is `undefined` outside the podium, so `PLACE_MEDALS[rank] || rank` is the whole rule.
export const PLACE_MEDALS = { 1: "\u{1F947}", 2: "\u{1F948}", 3: "\u{1F949}" };

// Competition / event `longDescription` cap. The short `description` stays at
// 2000 and is shown fixed on cards; this one holds the full announcement —
// roughly two A4 pages of text — and is collapsed by default everywhere.
export const MAX_LONG_DESCRIPTION = 4000;

// How many rows a route list renders at first, and how many more each tap on
// its "Továbbiak mutatása" footer adds — the map's route list and the
// Spraywall route list. A gym's fresh set is routinely more than a dozen
// routes; ten cut most walls off mid-set. Only the rendering is batched: the
// lists still load every route, because search, filters and sort run on the
// client.
export const ROUTE_LIST_PREVIEW = 35;

// Multi-gym plan feature gates and the climbing disciplines live in the
// shared package (the Cloud Functions read the same table and default);
// re-exported here so the client keeps one constants module.
export { PLAN_FEATURES, DISCIPLINES, DEFAULT_DISCIPLINES } from "@sendly/shared";

// NOTE: the former separate GRADE_SCALE_LEAD was merged into GRADE_SCALE above —
// there is now ONE universal ladder carrying font/v (boulder) AND french/uiaa
// (lead) labels per rung. Lead gyms map their difficulties to that same ladder.

// Current legal document versions — bump these when legal texts are updated
// to trigger re-acceptance for all users.
// MUST stay ISO date strings (YYYY-MM-DD): the consent check compares them
// lexicographically as "stored < current" (src/utils/legalConsent.js).
export const LEGAL_VERSIONS = {
  terms: "2026-06-10",
  privacy: "2026-06-10",
};

